import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export interface LiveScaleScoreProps {
  itemName: string;
  itemDescription?: string;
  itemIndex: number;
  totalItems: number;
  phaseTitle: string;
  /** Canonical definition of what this value means. Shown above the tool-specific scoring guidance. */
  valueDefinition?: string;
  /** What the criterion measures: for whom, against what, and its scale (migration 967). Shown under the title. */
  criterionReading?: ReactNode;
  phaseDescription: string;
  min?: number;
  max?: number;
  value: number | null;
  onValueChange: (value: number) => void;
  onNext: () => void;
  onBack?: () => void;
  isLastStep: boolean;
}

export function LiveScaleScore({
  itemName,
  itemDescription,
  itemIndex,
  totalItems,
  phaseTitle,
  valueDefinition,
  criterionReading,
  phaseDescription,
  min = 1,
  max = 5,
  value,
  onValueChange,
  onNext,
  onBack,
  isLastStep,
}: LiveScaleScoreProps) {
  const steps = Array.from({ length: max - min + 1 }, (_, i) => min + i);
  const scaleLayout = steps.length <= 6 ? 'flex' : steps.length === 7 ? 'grid-cols-7' : 'grid-cols-5';
  const sessionPct =
    totalItems > 0 ? Math.round(((itemIndex + (value != null ? 0.5 : 0)) / totalItems) * 100) : 0;

  return (
    <div className="max-w-lg">
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-4 mb-4">
        <div className="font-body text-[0.85rem] font-medium text-[var(--text-pri)]">{itemName}</div>
        {itemDescription && (
          <div className="font-body text-[0.75rem] text-[var(--text-ter)] mt-1">{itemDescription}</div>
        )}
      </div>

      <div className="font-heading text-[0.52rem] tracking-[0.14em] uppercase text-electric-mustard-600 mb-1">
        {phaseTitle}
      </div>
      {criterionReading && <div className="mb-1.5">{criterionReading}</div>}
      {valueDefinition && (
        <p className="font-body text-[0.78rem] text-[var(--text-sec)] mb-1.5 leading-relaxed">{valueDefinition}</p>
      )}
      <p className="font-body text-[0.78rem] text-[var(--text-ter)] mb-4 leading-relaxed">{phaseDescription}</p>

      {/* The scale is chosen at setup (up to 1–10). Wrapped freely, a phone
          showed 1–7 as six and a lone seven, and 1–10 as nine and a lone ten:
          seven values share one even row, more than seven make two rows of five. */}
      <div className={`mb-6 gap-2 ${scaleLayout === 'flex' ? 'flex flex-wrap' : `grid max-w-sm ${scaleLayout}`}`}>
        {steps.map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onValueChange(n)}
            className={`${scaleLayout === 'flex' ? 'min-w-[2.5rem] px-3' : 'w-full min-w-0 px-0'} h-10 border font-heading text-[0.7rem] transition-colors ${
              value === n
                ? 'border-alpine-green-500/50 bg-alpine-green-500/20 text-alpine-green-400'
                : 'border-[var(--rule-bright)] bg-[var(--bg-raised)] text-[var(--text-sec)] hover:border-[var(--slate)]'
            }`}
          >
            {n}
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-2 gap-3">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1 font-heading text-[0.55rem] tracking-[0.08em] uppercase text-[var(--text-ter)] hover:text-[var(--text-sec)]"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back
          </button>
        ) : (
          <span />
        )}
        <span className="font-heading text-[0.5rem] text-[var(--text-dim)]">{sessionPct}% session</span>
        <button
          type="button"
          onClick={onNext}
          disabled={value == null}
          className="inline-flex items-center gap-1 h-8 px-3 font-heading text-[0.55rem] tracking-[0.1em] uppercase text-alpine-green-400 border border-alpine-green-500/45 bg-alpine-green-500/15 hover:bg-alpine-green-500/25 disabled:opacity-40"
        >
          {isLastStep ? 'Done' : 'Next'}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
